import { message } from 'antd';
import cn from 'classnames';
import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { InputGroup } from '../form/InputGroup';
import { PlacesAutocomplete } from './PlacesAutocomplete';

function placeResultToFormValues(place: google.maps.places.PlaceResult): AddressFormValues {
  const values: AddressFormValues = {
    address1: '',
    address2: '',
    city: '',
    country: '',
    postCode: '',
    state: '',
  };
  // Get each component of the address from the place details,
  // and then fill-in the corresponding field on the form.
  // place.address_components are google.maps.GeocoderAddressComponent objects
  // which are documented at http://goo.gle/3l5i5Mr
  for (const component of place.address_components ?? []) {
    const componentType = component.types[0];

    switch (componentType) {
      case 'street_number': {
        values.address1 = `${component.long_name} ${values.address1}`;
        break;
      }

      case 'route': {
        values.address1 += component.short_name;
        break;
      }

      case 'postal_code': {
        values.postCode = `${component.long_name}${values.postCode}`;
        break;
      }

      case 'postal_code_suffix': {
        values.postCode = `${values.postCode}-${component.long_name}`;
        break;
      }
      case 'locality':
        values.city = component.long_name;
        break;

      case 'administrative_area_level_1': {
        values.state = component.short_name;
        break;
      }
      case 'country':
        values.country = component.long_name;
        break;
    }
  }

  return values;
}

export type AddressFormValues = {
  address1: string | null;
  address2: string | null;
  state: string | null;
  city: string | null;
  country: string | null;
  postCode: string | null;
};

const requiredFields: { [key in keyof AddressFormValues]: boolean } = {
  address1: true,
  address2: false,
  state: true,
  city: true,
  country: true,
  postCode: true,
};

function getRequiredRule(key: keyof AddressFormValues): string | false {
  return requiredFields[key] ? 'Required.' : false;
}

export const addressFormFields = Object.keys(requiredFields) as Array<keyof AddressFormValues>;

const labelClass = cn(InputGroup.labelClass, 'w-40');

type AddressFormProps = {
  defaultValues: AddressFormValues;
  onSubmit(values: AddressFormValues): void;
  isSubmitting: boolean;
  onCancel(): void;
  canCancel: boolean;
};

/**
 * Needs Google Maps/Places api loaded beforehand.
 */
export function AddressForm({
  defaultValues,
  onSubmit,
  isSubmitting,
  onCancel,
  canCancel,
}: AddressFormProps) {
  const generateSessionToken = () => new google.maps.places.AutocompleteSessionToken();
  const [sessionToken, setSessionToken] = useState(generateSessionToken);
  const [placeId, setPlaceId] = useState<string | null>(null);
  const form = useForm({
    defaultValues,
  });
  const map = useMemo(() => new google.maps.Map(document.createElement('div')), []);
  const service = useMemo(() => new google.maps.places.PlacesService(map), []);

  // TODO: powered by google logo
  useEffect(() => {
    if (placeId == null) return;
    let cancelled = false;

    service.getDetails(
      {
        placeId,
        fields: ['address_components'],
        sessionToken,
      },
      (place, status) => {
        if (cancelled) return;
        if (status !== google.maps.places.PlacesServiceStatus.OK || place == null) {
          message.error('Address service error.');
          console.error('Address service error.', status, place);
          return;
        }

        setSessionToken(generateSessionToken());

        const values = placeResultToFormValues(place);

        for (const [key, value] of Object.entries(values)) {
          form.setValue(key as keyof typeof values, value);
        }
      },
    );

    return () => {
      cancelled = true;
    };
  }, [placeId]);

  return (
    <form className="w-[500px] max-w-full text-left" onSubmit={form.handleSubmit(onSubmit)}>
      <div className="text-xl font-bold text-center p-6">Please provide your shipping address</div>
      <div className="border-t border-b p-6 border-gray-cb space-y-4">
        <InputGroup
          label="Search Your Location:"
          labelClass={cn(InputGroup.labelClass, 'mb-2')}
          renderInput={() => (
            <PlacesAutocomplete onChange={setPlaceId} sessionToken={sessionToken} />
          )}
        />
        <InputGroup
          row
          form={form}
          label="Street Address 1"
          labelClass={labelClass}
          name="address1"
          required={requiredFields.address1}
          register={{ required: getRequiredRule('address1') }}
        />
        <InputGroup
          row
          form={form}
          label="Street Address 2"
          labelClass={labelClass}
          name="address2"
          required={requiredFields.address2}
          register={{ required: getRequiredRule('address2') }}
        />
        <InputGroup
          row
          form={form}
          label="City"
          labelClass={labelClass}
          name="city"
          required={requiredFields.city}
          register={{ required: getRequiredRule('city') }}
        />
        <InputGroup
          row
          form={form}
          label="State"
          labelClass={labelClass}
          name="state"
          required={requiredFields.state}
          register={{ required: getRequiredRule('state') }}
        />
        <InputGroup
          row
          form={form}
          label="Postcode"
          labelClass={labelClass}
          name="postCode"
          required={requiredFields.postCode}
          register={{
            required: getRequiredRule('postCode'),
          }}
        />
        <InputGroup
          row
          form={form}
          label="Country"
          labelClass={labelClass}
          name="country"
          required={requiredFields.country}
          register={{ required: getRequiredRule('country') }}
        />
      </div>
      <div className="p-6 flex-center space-x-2">
        <button
          type="submit"
          className={cn(
            'btn btn-primary h-10 rounded-full text-lg px-5',
            isSubmitting && 'opacity-50',
          )}
          disabled={isSubmitting}
        >
          Save Address
        </button>
        {canCancel && (
          <button
            type="button"
            className={cn(
              'btn btn-primary h-10 rounded-full text-lg px-5',
              isSubmitting && 'opacity-50',
            )}
            onClick={onCancel}
          >
            Cancel
          </button>
        )}
      </div>
    </form>
  );
}
